import { useState } from "react";
import { motion } from "framer-motion";
import { Store, GraduationCap, Cake, Send, CheckCircle2, PartyPopper } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPES = [
  { value: "vendor", label: "Vendor Event", icon: Store },
  { value: "school", label: "School Event", icon: GraduationCap },
  { value: "birthday", label: "Birthday Party", icon: Cake },
];

const GUEST_OPTIONS = ["Under 50", "50-100", "100-250", "250+"];

const today = new Date().toISOString().split("T")[0];

const inputCls =
  "w-full rounded-xl border-[3px] border-ink bg-white px-4 py-3 text-base font-semibold text-ink placeholder:font-semibold placeholder:text-ink/40 shadow-[inset_2px_2px_0_0_rgba(28,25,23,0.08)] transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-sunburst/60 focus:-translate-y-0.5";

const labelCls = "mb-1.5 block text-xs font-black uppercase tracking-[0.14em] text-ink/70";

export default function BookingForm({ eventType, onEventTypeChange }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    event_date: "",
    guests: "",
    location: "",
    message: "",
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      const res = await fetch(`${API}/bookings`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, event_type: eventType }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Something went wrong");
      setSent(true);
      toast.success("Booking request sent! We'll reply soon.");
    } catch (err) {
      toast.error(err.message || "Couldn't send your request. Please try again");
    } finally {
      setSending(false);
    }
  };

  const reset = () => {
    setForm({ name: "", email: "", phone: "", event_date: "", guests: "", location: "", message: "" });
    setSent(false);
  };

  return (
    <section id="book" data-testid="booking-section" className="dots-bg relative overflow-hidden bg-sunburst py-20 lg:py-28">
      <div className="mx-auto max-w-3xl px-4 sm:px-6">
        <div className="mb-10 text-center">
          <p className="text-sm font-black uppercase tracking-[0.24em] text-tomato">Let's party</p>
          <h2 className="font-display mt-3 text-4xl font-bold sm:text-5xl">
            Book Sy Sy's <span className="font-logo text-tomato">Snacks</span>
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-base font-semibold text-ink/75">
            Tell us about your event and we'll get back to you from{" "}
            <span className="font-black text-ink">sysyssnacks@gmail.com</span> within a day or two.
          </p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.1 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="rounded-[2rem] border-4 border-ink bg-white p-6 shadow-popXl sm:p-10"
        >
          {sent ? (
            <div data-testid="booking-success-panel" className="py-8 text-center">
              <span className="mx-auto flex h-20 w-20 items-center justify-center rounded-full border-4 border-ink bg-sunburst shadow-pop">
                <CheckCircle2 className="h-10 w-10 text-ink" strokeWidth={2.5} />
              </span>
              <h3 className="font-display mt-6 text-3xl font-bold">Request received!</h3>
              <p className="mx-auto mt-3 max-w-sm text-base font-semibold text-ink/75">
                Thanks {form.name.split(" ")[0]}! We'll email you at{" "}
                <span className="font-black text-ink">{form.email}</span> to lock in your{" "}
                {TYPES.find((t) => t.value === eventType)?.label}.
              </p>
              <button
                type="button"
                data-testid="booking-another-button"
                onClick={reset}
                className="mt-8 inline-flex items-center gap-2 rounded-full border-[3px] border-ink bg-tang px-7 py-3 text-sm font-black uppercase tracking-wide text-white shadow-pop transition-all duration-200 hover:-translate-y-0.5 hover:bg-tang-dark"
              >
                <PartyPopper className="h-4 w-4" strokeWidth={2.75} /> Book another event
              </button>
            </div>
          ) : (
            <form data-testid="booking-form" onSubmit={submit} className="space-y-6">
              <fieldset>
                <legend className={labelCls}>What kind of event? *</legend>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  {TYPES.map((t) => {
                    const active = eventType === t.value;
                    return (
                      <button
                        key={t.value}
                        type="button"
                        data-testid={`event-type-chip-${t.value}`}
                        aria-pressed={active}
                        onClick={() => onEventTypeChange(t.value)}
                        className={`flex items-center justify-center gap-2 rounded-xl border-[3px] px-4 py-3.5 text-sm font-black uppercase tracking-wide transition-all duration-200 ${
                          active
                            ? "border-ink bg-tang text-white shadow-popSm"
                            : "border-ink/25 bg-cream text-ink/70 hover:border-ink hover:bg-sunburst/30"
                        }`}
                      >
                        <t.icon className="h-5 w-5" strokeWidth={2.75} />
                        {t.label}
                      </button>
                    );
                  })}
                </div>
              </fieldset>

              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label className={labelCls} htmlFor="bk-name">Your name *</label>
                  <input id="bk-name" data-testid="booking-name-input" required minLength={2} value={form.name} onChange={set("name")} className={inputCls} placeholder="Jane Doe" />
                </div>
                <div>
                  <label className={labelCls} htmlFor="bk-email">Email *</label>
                  <input id="bk-email" data-testid="booking-email-input" required type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="you@email.com" />
                </div>
                <div>
                  <label className={labelCls} htmlFor="bk-phone">Phone</label>
                  <input id="bk-phone" data-testid="booking-phone-input" type="tel" value={form.phone} onChange={set("phone")} className={inputCls} placeholder="(661) 555-0123" />
                </div>
                <div>
                  <label className={labelCls} htmlFor="bk-date">Event date *</label>
                  <input id="bk-date" data-testid="booking-date-input" required type="date" min={today} value={form.event_date} onChange={set("event_date")} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls} htmlFor="bk-guests">How many guests?</label>
                  <select id="bk-guests" data-testid="booking-guests-select" value={form.guests} onChange={set("guests")} className={inputCls}>
                    <option value="">Pick a range…</option>
                    {GUEST_OPTIONS.map((g) => (
                      <option key={g} value={g}>{g}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelCls} htmlFor="bk-location">Event location *</label>
                  <input id="bk-location" data-testid="booking-location-input" required value={form.location} onChange={set("location")} className={inputCls} placeholder="Park, school or address in Bakersfield" />
                </div>
              </div>

              <div>
                <label className={labelCls} htmlFor="bk-message">Tell us more</label>
                <textarea id="bk-message" data-testid="booking-message-input" rows={4} value={form.message} onChange={set("message")} className={`${inputCls} resize-none`} placeholder="What are we celebrating? Any time details we should know?" />
              </div>

              <button
                type="submit"
                data-testid="booking-submit-button"
                disabled={sending}
                className="group flex w-full items-center justify-center gap-2 rounded-full border-[3px] border-ink bg-tomato px-8 py-4 text-lg font-black uppercase tracking-wide text-white shadow-pop transition-all duration-200 hover:-translate-y-1 hover:bg-tomato-dark hover:shadow-popLg disabled:cursor-not-allowed disabled:opacity-60"
              >
                {sending ? (
                  "Sending…"
                ) : (
                  <>
                    Send Booking Request
                    <Send className="h-5 w-5 transition-transform duration-200 group-hover:translate-x-1 group-hover:-translate-y-0.5" strokeWidth={2.75} />
                  </>
                )}
              </button>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
}
